import React, { useState, useCallback, useMemo } from 'react'
import {
	Button,
	Box,
	Typography,
	Tabs,
	Tab,
	Alert,
	Divider,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
	LoginContainer,
	GridBackground,
	LoginPaper,
} from 'components/ApplicationStyles'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import SearchIcon from '@mui/icons-material/Search'
import LockResetIcon from '@mui/icons-material/LockReset'
import { Link, useNavigate } from 'react-router-dom'
import {
	alertModal,
	autoCellPhone,
	confirmAlert,
	validEmail,
} from '../../util/util'
import axios from 'axios'

// 스타일링된 TextField 컴포넌트
const TechTextField = styled('input')(({ theme }) => ({
	width: '100%',
	padding: '16px',
	paddingLeft: '50px',
	fontSize: '16px',
	backgroundColor: 'rgba(255, 255, 255, 0.1)',
	border: '1px solid rgba(255, 255, 255, 0.3)',
	borderRadius: '12px',
	color: '#fff',
	outline: 'none',
	transition: 'all 0.3s ease',
	'&::placeholder': {
		color: 'rgba(255, 255, 255, 0.5)',
	},
	'&:focus': {
		borderColor: '#4C51D3',
		backgroundColor: 'rgba(255, 255, 255, 0.15)',
		boxShadow: '0 0 0 2px rgba(76, 81, 211, 0.3)',
	},
	'&:hover': {
		borderColor: 'rgba(255, 255, 255, 0.4)',
	},
}))

const InputContainer = styled(Box)({
	position: 'relative',
	marginBottom: '16px',
})

const InputIcon = styled(Box)({
	position: 'absolute',
	left: '16px',
	top: '50%',
	transform: 'translateY(-50%)',
	zIndex: 1,
	color: 'rgba(255, 255, 255, 0.7)',
})

const StyledButton = styled(Button)({
	background: 'linear-gradient(135deg, #4C51D3 0%, #6366f1 100%)',
	color: '#fff',
	padding: '12px 24px',
	borderRadius: '12px',
	textTransform: 'none',
	fontWeight: '600',
	fontSize: '16px',
	boxShadow: '0 4px 15px rgba(76, 81, 211, 0.4)',
	'&:hover': {
		background: 'linear-gradient(135deg, #3b40c7 0%, #4f46e5 100%)',
		boxShadow: '0 6px 20px rgba(76, 81, 211, 0.5)',
	},
	'&:disabled': {
		background: 'rgba(255, 255, 255, 0.1)',
		color: 'rgba(255, 255, 255, 0.3)',
		boxShadow: 'none',
	},
})

interface TabPanelProps {
	children?: React.ReactNode
	index: number
	value: number
}

const TabPanel = (props: TabPanelProps) => {
	const { children, value, index, ...other } = props

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`find-tabpanel-${index}`}
			aria-labelledby={`find-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
		</div>
	)
}

const Find = () => {
	const navigate = useNavigate()
	const [tabValue, setTabValue] = useState(0)
	const [isLoading, setIsLoading] = useState(false)
	const [message, setMessage] = useState('')
	const [messageType, setMessageType] = useState<'success' | 'error'>('success')
	const [isMailSent, setIsMailSent] = useState(false)

	// 아이디 찾기 상태
	const [findIdData, setFindIdData] = useState({
		name: '',
		email: '',
	})

	// 비밀번호 찾기 상태
	const [findPasswordData, setFindPasswordData] = useState({
		email: '',
	})

	const handleTabChange = useCallback(
		(event: React.SyntheticEvent, newValue: number) => {
			setTabValue(newValue)
			setMessage('')
		},
		[]
	)

	// 아이디 찾기 입력 처리
	const handleIdInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = e.target
			let processedValue = value

			if (name === 'phone') {
				processedValue = autoCellPhone(value)
			}

			setFindIdData((prev) => ({
				...prev,
				[name]: processedValue,
			}))
			setMessage('')
		},
		[]
	)

	// 비밀번호 찾기 입력 처리
	const handlePasswordInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = e.target
			setFindPasswordData((prev) => ({
				...prev,
				[name]: value,
			}))
			setMessage('')
		},
		[]
	)

	// 아이디 찾기 제출
	const handleFindId = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault()

			if (!findIdData.name.trim()) {
				setMessage('이름을 입력해주세요.')
				setMessageType('error')
				return
			}

			if (!findIdData.email.trim() || !validEmail(findIdData.email)) {
				setMessage('올바른 이메일을 입력해주세요.')
				setMessageType('error')
				return
			}

			setIsLoading(true)
			setMessage('')

			console.log(findIdData)

			try {
				const response = await axios
					.post(`${process.env.REACT_APP_HOST}/user/find/userId`, {
						name: findIdData.name,
						email: findIdData.email,
					})
					.then((res) => res.data)
					.catch((error) => {
						console.log(error)
						if (
							error.response &&
							error.response.data &&
							error.response.data.message
						) {
							throw new Error(error.response.data.message)
						}
					})

				console.log(response)

				if (response.success && response.data) {
					setMessage(response.data)
					setMessageType('success')
					setIsLoading(false)
				} else {
					setMessage(response.data)
					setMessageType('error')
					setIsLoading(false)
				}
			} catch (error) {
				console.error('아이디 찾기 오류:', error)
				setMessage('입력하신 정보로 등록된 아이디를 찾을 수 없습니다.')
				setMessageType('error')
				setIsLoading(false)
			} finally {
				setIsLoading(false)
			}
		},
		[findIdData]
	)

	// 비밀번호 찾기 제출
	const handleFindPassword = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault()

			if (!findPasswordData.email.trim()) {
				setMessage('이메일을 입력해주세요.')
				setMessageType('error')
				return
			}

			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			if (!emailRegex.test(findPasswordData.email)) {
				setMessage('올바른 이메일 형식을 입력해주세요.')
				setMessageType('error')
				return
			}

			setIsLoading(true)
			setMessage('')

			try {
				const response = await axios
					.post(`${process.env.REACT_APP_HOST}/auth/password/resetLink`, {
						email: findPasswordData.email,
					})
					.then((res) => res.data)
					.catch((error) => {
						if (
							error.response &&
							error.response.data &&
							error.response.data.message
						) {
							throw new Error(error.response.data.message)
						}
					})

				console.log(response)

				if (response.success && response.data) {
					setMessage(response.data)
					setMessageType('success')
					setIsLoading(false)
					setIsMailSent(true)
					confirmAlert(
						response.data,
						() => {
							navigate('/')
						},
						() => {}
					)
				} else {
					setMessage(response.data)
					setMessageType('error')
					setIsLoading(false)
				}
			} catch (error) {
				console.error('비밀번호 찾기 오류:', error)
				if (axios.isAxiosError(error)) {
					console.log(error.response)
				}
				setMessage('입력하신 이메일로 등록된 계정을 찾을 수 없습니다.')
				setMessageType('error')
				setIsLoading(false)
			} finally {
				setIsLoading(false)
			}
		},
		[findPasswordData]
	)

	const iconColor = useMemo(() => ({ color: 'rgba(255, 255, 255, 0.7)' }), [])

	return (
		<LoginContainer maxWidth={false}>
			<GridBackground />
			<LoginPaper elevation={0}>
				<Box sx={{ width: '100%' }}>
					{/* 헤더 */}
					<Box sx={{ textAlign: 'center', mb: 3 }}>
						<Typography
							variant="h4"
							sx={{
								color: '#fff',
								fontWeight: 'bold',
								mb: 1,
								textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
							}}
						>
							계정 찾기
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: 'rgba(255, 255, 255, 0.7)',
							}}
						>
							아이디 또는 비밀번호를 찾아보세요
						</Typography>
					</Box>

					{/* 탭 메뉴 */}
					<Tabs
						value={tabValue}
						onChange={handleTabChange}
						centered
						sx={{
							mb: 2,
							'& .MuiTabs-indicator': {
								backgroundColor: '#4C51D3',
								height: '3px',
								borderRadius: '2px',
							},
							'& .MuiTab-root': {
								color: 'rgba(255, 255, 255, 0.7)',
								textTransform: 'none',
								fontWeight: '600',
								fontSize: '16px',
								'&.Mui-selected': {
									color: '#4C51D3',
								},
							},
						}}
					>
						<Tab
							icon={<PersonIcon />}
							iconPosition="start"
							label="아이디 찾기"
						/>
						<Tab
							icon={<LockResetIcon />}
							iconPosition="start"
							label="비밀번호 찾기"
						/>
					</Tabs>

					{/* 메시지 표시 */}
					{message && (
						<Alert
							severity={messageType}
							sx={{
								mb: 2,
								backgroundColor:
									messageType === 'success'
										? 'rgba(76, 175, 80, 0.1)'
										: 'rgba(244, 67, 54, 0.1)',
								color: messageType === 'success' ? '#4CAF50' : '#f44336',
								border: `1px solid ${
									messageType === 'success'
										? 'rgba(76, 175, 80, 0.3)'
										: 'rgba(244, 67, 54, 0.3)'
								}`,
								borderRadius: '8px',
							}}
						>
							{message}
						</Alert>
					)}

					{/* 아이디 찾기 패널 */}
					<TabPanel
						value={tabValue}
						index={0}
					>
						<form onSubmit={handleFindId}>
							<Box sx={{ mb: 3 }}>
								<Typography
									variant="h6"
									sx={{ color: '#fff', mb: 2, fontWeight: '600' }}
								>
									이름과 이메일로 아이디 찾기
								</Typography>

								{/* 이름 입력 */}
								<InputContainer>
									<InputIcon>
										<PersonIcon sx={iconColor} />
									</InputIcon>
									<TechTextField
										name="name"
										type="text"
										placeholder="이름을 입력하세요"
										value={findIdData.name}
										onChange={handleIdInputChange}
										maxLength={20}
									/>
								</InputContainer>

								{/* 전화번호 입력 */}
								<InputContainer>
									<InputIcon>
										<EmailIcon sx={iconColor} />
									</InputIcon>
									<TechTextField
										name="email"
										type="text"
										placeholder="이메일을 입력하세요"
										value={findIdData.email}
										onChange={handleIdInputChange}
									/>
								</InputContainer>

								<StyledButton
									type="submit"
									fullWidth
									disabled={isLoading}
									startIcon={<SearchIcon />}
								>
									{isLoading ? '검색 중...' : '아이디 찾기'}
								</StyledButton>
							</Box>
						</form>
					</TabPanel>

					{/* 비밀번호 찾기 패널 */}
					<TabPanel
						value={tabValue}
						index={1}
					>
						<form onSubmit={handleFindPassword}>
							<Box sx={{ mb: 3 }}>
								<Typography
									variant="h6"
									sx={{ color: '#fff', mb: 2, fontWeight: '600' }}
								>
									이메일로 비밀번호 재설정
								</Typography>

								{/* 이메일 입력 */}
								<InputContainer>
									<InputIcon>
										<EmailIcon sx={iconColor} />
									</InputIcon>
									<TechTextField
										name="email"
										type="email"
										placeholder="이메일을 입력하세요"
										value={findPasswordData.email}
										onChange={handlePasswordInputChange}
									/>
								</InputContainer>

								<StyledButton
									type="submit"
									fullWidth
									disabled={isLoading}
									startIcon={<LockResetIcon />}
								>
									{isLoading
										? isMailSent
											? '이메일 발송완료'
											: '발송 중...'
										: '비밀번호 재설정 이메일 발송'}
								</StyledButton>
							</Box>
						</form>
					</TabPanel>

					{/* 구분선 */}
					<Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

					{/* 하단 링크 */}
					<Box sx={{ textAlign: 'center' }}>
						<Typography
							variant="body2"
							sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}
						>
							계정을 찾으셨나요?{' '}
							<Link
								to="/"
								style={{
									color: '#4C51D3',
									textDecoration: 'none',
									fontWeight: 'bold',
								}}
							>
								로그인하기
							</Link>
						</Typography>

						<Typography
							variant="body2"
							sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
						>
							계정이 없으신가요?{' '}
							<Link
								to="/user/join"
								style={{
									color: '#4C51D3',
									textDecoration: 'none',
									fontWeight: 'bold',
								}}
							>
								회원가입
							</Link>
						</Typography>
					</Box>

					{/* 추가 정보 */}
					<Typography
						variant="caption"
						sx={{
							color: 'rgba(255, 255, 255, 0.5)',
							textAlign: 'center',
							display: 'block',
							mt: 3,
						}}
					>
						© 2025 MindCline. All rights reserved.
					</Typography>
				</Box>
			</LoginPaper>
		</LoginContainer>
	)
}

export default Find
